import { notify } from '@/lib/toast';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Car, ChefHat, ClipboardList, MonitorSmartphone, Plus, Clock, Users, MapPin } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Combobox } from '@/components/ui/combobox';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { FilterChips } from '@/components/ui/filter-chips';
import { useAuthStore } from '@/stores/auth.store';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import type { DiningOrder, RestaurantTable } from '@/types/restaurant';


const TYPE_COLORS: Record<string, string> = {
  DINE_IN:  'bg-info/10 text-info-text',
  TAKEAWAY: 'bg-primary/10 text-primary-text',
  DELIVERY: 'bg-warning/10 text-warning-text',
};

const STATUS_COLORS: Record<string, string> = {
  OPEN:      'bg-success/10 text-success-text',
  PREPARING: 'bg-warning/10 text-warning-text',
  READY:     'bg-success/10 text-success-text',
  SERVED:    'bg-info/10 text-info-text',
  BILLED:    'bg-muted text-muted-foreground',
  COMPLETED: 'bg-muted text-muted-foreground',
  VOIDED:    'bg-destructive/10 text-destructive-text',
};

const ACTIVE_STATUSES = ['OPEN', 'PREPARING', 'READY', 'SERVED', 'BILLED'];

function elapsed(createdAt: string): string {
  const mins = Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
}

type DiningOrderType = DiningOrder['type'];

interface StaffUserRow {
  id: string;
  firstName: string;
  lastName: string;
}

interface CreateDiningOrderPayload {
  type: DiningOrderType;
  tableId?: string;
  guestCount: number;
  notes: string;
  deliveryAddress: string;
  locationId?: string;
  carSlot: string;
  serverId?: string;
}

const emptyOrderForm = {
  type: 'DINE_IN' as DiningOrderType,
  tableId: '',
  guestCount: 1,
  notes: '',
  deliveryAddress: '',
  locationId: '',
  carSlot: '',
  serverId: '',
};

export function DiningOrdersPage() {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [page, setPage] = useState(1);
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [orderForm, setOrderForm] = useState(emptyOrderForm);

  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const { t } = useTranslation('restaurant');
  const canWrite = hasPermission('restaurant:write') || hasPermission('restaurant:*');
  // POS-I314 / POS-I315: entry points to the full-screen Kitchen Display and Self-Service Kiosk.
  const canKitchen = hasPermission('kitchen:read');
  const canKiosk = hasPermission('pos:create');
  const TYPE_LABELS: Record<string, string> = {
    DINE_IN:  t('dineIn'),
    TAKEAWAY: t('takeaway'),
    DELIVERY: t('delivery'),
  };

  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['dining-orders', typeFilter, statusFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      const statuses = statusFilter === 'ALL' ? ACTIVE_STATUSES : [statusFilter];
      params.set('status', statuses.join(','));
      if (typeFilter !== 'ALL') params.set('type', typeFilter);
      params.set('page', String(page));
      params.set('limit', '20');
      const res = await api.get(`/dining-orders?${params}`);
      return res.data;
    },
    refetchInterval: 300000, // 5 min safety-net; real-time via WebSocket
  });
  const orders: DiningOrder[] = ordersData?.data ?? (Array.isArray(ordersData) ? ordersData : []);
  const ordersTotal: number = ordersData?.total ?? orders.length;

  // Real-time: refresh dining orders on any order event
  const handleOrderUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dining-orders'] });
  }, [queryClient]);
  useSocketEvent('dining_order.created', handleOrderUpdate);
  useSocketEvent('dining_order.updated', handleOrderUpdate);
  useSocketEvent('dining_order.billed', handleOrderUpdate);
  useSocketEvent('dining_order.voided', handleOrderUpdate);

  const { data: tablesData } = useQuery<RestaurantTable[]>({
    queryKey: ['available-tables'],
    queryFn: () => api.get('/restaurant/tables?status=AVAILABLE').then((r) => r.data?.data ?? r.data ?? []),
    enabled: newOrderOpen,
  });

  // Locations only needed for TAKEAWAY/DELIVERY orders (no table to derive location from)
  const { data: locations } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['locations'],
    queryFn: () => api.get('/locations').then((r) => r.data?.data ?? r.data ?? []),
    enabled: newOrderOpen && orderForm.type !== 'DINE_IN',
  });

  // Staff users for waiter/server assignment
  const { data: staffUsers } = useQuery<{ id: string; firstName: string; lastName: string }[]>({
    queryKey: ['staff-users'],
    queryFn: () => api.get('/users?limit=200').then((r) => {
      const list = r.data?.data ?? r.data ?? [];
      return list.map((u: StaffUserRow) => ({ id: u.id, firstName: u.firstName, lastName: u.lastName }));
    }),
    enabled: newOrderOpen,
  });

  const createOrderMutation = useMutation({
    mutationFn: (data: CreateDiningOrderPayload) => api.post('/dining-orders', data),
    onSuccess: (res) => {
      toast.success(t('orderCreated'));
      queryClient.invalidateQueries({ queryKey: ['dining-orders'] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      setNewOrderOpen(false);
      setOrderForm(emptyOrderForm);
      navigate(`/restaurant/orders/${res.data.id}`);
    },
    onError: (err) => notify.error(err, t('failedToCreateOrder')),
  });

  const handleCreateOrder = () => {
    if (orderForm.type === 'DINE_IN' && !orderForm.tableId) {
      toast.error(t('selectTableRequired'));
      return;
    }
    createOrderMutation.mutate({
      ...orderForm,
      guestCount: Number(orderForm.guestCount),
      tableId: orderForm.tableId || undefined,
      locationId: orderForm.locationId || undefined,
      serverId: orderForm.serverId || undefined,
    });
  };

  const typeTabs = [
    { id: 'ALL', label: t('all') },
    { id: 'DINE_IN', label: t('dineIn') },
    { id: 'TAKEAWAY', label: t('takeaway') },
    { id: 'DELIVERY', label: t('delivery') },
  ];

  const statusChips = [
    { id: 'ALL', label: t('all') },
    { id: 'OPEN', label: t('open') },
    { id: 'PREPARING', label: t('preparing') },
    { id: 'READY', label: t('ready') },
    { id: 'SERVED', label: t('served') },
    { id: 'BILLED', label: t('billed') },
  ];

  return (
    <div className="space-y-6 pb-24 lg:pb-6 page-enter">
      <PageHeader
        title={t('activeOrders')}
        subtitle={t('ordersSubtitle')}
        action={
          canWrite || canKitchen || canKiosk ? (
            <div className="flex flex-wrap items-center gap-2">
              {canKitchen && (
                <Button variant="outline" className="h-11" onClick={() => window.open('/kitchen', '_blank', 'noopener')}>
                  <ChefHat className="me-2 h-4 w-4" />
                  {t('kitchen')}
                </Button>
              )}
              {canKiosk && (
                <Button variant="outline" className="h-11" onClick={() => window.open('/kiosk', '_blank', 'noopener')}>
                  <MonitorSmartphone className="me-2 h-4 w-4" />
                  {t('selfServiceKiosk')}
                </Button>
              )}
              {canWrite && (
                <Button className="h-11" onClick={() => setNewOrderOpen(true)}>
                  <Plus className="me-2 h-4 w-4" />
                  {t('newOrder')}
                </Button>
              )}
            </div>
          ) : undefined
        }
      />

      <FilterChips
        chips={typeTabs.map((tab) => ({ key: tab.id, label: tab.label }))}
        value={typeFilter}
        onChange={setTypeFilter}
        size="touch"
      />

      <FilterChips
        chips={statusChips.map((chip) => ({ key: chip.id, label: chip.label }))}
        value={statusFilter}
        onChange={setStatusFilter}
        variant="outline"
        size="touch"
        wrap
      />

      {/* Orders grid */}
      {isLoading ? (
        <SkeletonTable rows={6} cols={5} />
      ) : !orders?.length ? (
        <EmptyState
          icon={ClipboardList}
          title={t('noActiveOrders')}
          description={t('noOrdersMatchFilter')}
          action={canWrite ? { label: t('newOrder'), onClick: () => setNewOrderOpen(true) } : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {orders.map((order) => (
            <Card
              key={order.id}
              className="cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              onClick={() => navigate(`/restaurant/orders/${order.id}`)}
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-lg">#{order.orderNo}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-sm font-medium ${TYPE_COLORS[order.type]}`}>
                    {TYPE_LABELS[order.type]}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-sm font-medium ${STATUS_COLORS[order.status]}`}>
                    {order.status}
                  </span>
                  {order.table && (
                    <span className="text-base font-semibold text-muted-foreground">Table #{order.table.tableNo}</span>
                  )}
                  {order.carSlot && order.type === 'TAKEAWAY' && (
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary-text"><Car className="h-4 w-4" /> {order.carSlot}</span>
                  )}
                  {order.location && (
                    <span className="flex items-center gap-0.5 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      {order.location.name}
                    </span>
                  )}
                </div>
                {(order.customerName || order.customerPhone) && (
                  <p className="text-sm text-foreground truncate">
                    {[order.customerName, order.customerPhone].filter(Boolean).join(' · ')}
                  </p>
                )}
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {order.guestCount} {t('guestsLabel')}
                  </span>
                  <span className="flex items-center gap-1">
                    <ClipboardList className="h-4 w-4" />
                    {order._count?.items ?? order.items?.length ?? 0} {t('itemsLabel')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {elapsed(order.createdAt)}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {ordersTotal > 20 && (
        <PaginationBar page={page} pageSize={20} total={ordersTotal} onPageChange={setPage} />
      )}

      {/* {t('newOrder')} dialog */}
      <Dialog
        visible={newOrderOpen}
        onHide={() => { setNewOrderOpen(false); setOrderForm(emptyOrderForm); }}
        header={t('newOrder')}
        className="w-full max-w-lg"
        modal
        breakpoints={{ '960px': '80vw', '641px': '95vw' }}
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">{t('orderType')} <span className="text-destructive-text">*</span></Label>
            <Combobox
              options={[
                { value: 'DINE_IN', label: t('dineIn') },
                { value: 'TAKEAWAY', label: t('takeaway') },
                { value: 'DELIVERY', label: t('delivery') },
              ]}
              value={orderForm.type}
              onChange={(val) => setOrderForm({ ...orderForm, type: (val as DiningOrderType) || 'DINE_IN' })}
              placeholder={t('selectOrderType')}
            />
          </div>

          {orderForm.type === 'DINE_IN' && (
            <div>
              <Label className="mb-1.5 block">{t('table')} <span className="text-destructive-text">*</span></Label>
              <Combobox
                options={(tablesData ?? []).map((tbl) => ({ value: tbl.id, label: `Table #${tbl.tableNo} (${tbl.capacity} seats)` }))}
                value={orderForm.tableId}
                onChange={(val) => setOrderForm({ ...orderForm, tableId: val ?? '' })}
                placeholder={t('selectTablePlaceholder')}
                emptyText={t('noTablesFound')}
              />
            </div>
          )}

          {orderForm.type === 'TAKEAWAY' && (
            <div>
              <Label className="mb-1.5 block">{t('carSlot')}</Label>
              <Input
                placeholder={t('carSlotPlaceholder')}
                value={orderForm.carSlot}
                onChange={(e) => setOrderForm({ ...orderForm, carSlot: e.target.value })}
              />
            </div>
          )}

          {orderForm.type === 'DELIVERY' && (
            <div>
              <Label className="mb-1.5 block">{t('deliveryAddress')}</Label>
              <Input
                placeholder={t('deliveryAddress')}
                value={orderForm.deliveryAddress}
                onChange={(e) => setOrderForm({ ...orderForm, deliveryAddress: e.target.value })}
              />
            </div>
          )}

          {/* Location: only for TAKEAWAY/DELIVERY — DINE_IN inherits from the table */}
          {orderForm.type !== 'DINE_IN' && locations && locations.length > 1 && (
            <div>
              <Label className="mb-1.5 block">{t('location')}</Label>
              <Dropdown
                className="w-full"
                value={orderForm.locationId}
                onChange={(e) => setOrderForm({ ...orderForm, locationId: e.value })}
                options={[
                  { label: t('noSpecificLocation'), value: '' },
                  ...locations.map((l) => ({ label: l.name, value: l.id })),
                ]}
                optionLabel="label"
                optionValue="value"
                placeholder={t('noSpecificLocation')}
              />
            </div>
          )}

          <div>
            <Label className="mb-1.5 block">{t('guestCount')}</Label>
            <Input
              type="number"
              min="1"
              step="1"
              value={orderForm.guestCount}
              onChange={(e) => setOrderForm({ ...orderForm, guestCount: Number(e.target.value) })}
            />
          </div>

          {/* Waiter / Server assignment */}
          {staffUsers && staffUsers.length > 0 && (
            <div>
              <Label className="mb-1.5 block">{t('waiterServer')}</Label>
              <Combobox
                options={staffUsers.map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName}`.trim() }))}
                value={orderForm.serverId}
                onChange={(val) => setOrderForm({ ...orderForm, serverId: val ?? '' })}
                placeholder={t('selectWaiterPlaceholder')}
                emptyText={t('noStaffFound')}
              />
              <p className="text-xs text-muted-foreground mt-1">{t('waiterHelpText')}</p>
            </div>
          )}

          <div>
            <Label className="mb-1.5 block">{t('notes')}</Label>
            <Input
              placeholder={t('optionalOrderNotes')}
              value={orderForm.notes}
              onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => { setNewOrderOpen(false); setOrderForm(emptyOrderForm); }}>
              {t('cancel')}
            </Button>
            <Button
              className="flex-1"
              onClick={handleCreateOrder}
              disabled={createOrderMutation.isPending}
            >
              {createOrderMutation.isPending ? t('creating') : t('createOrder')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
